import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, Crown } from 'lucide-react';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/premium');
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          updateUser({ is_premium: true });
          toast.success('Pagamento confirmado! Agora é Premium!');
        } else if (res.data.status === 'expired') {
          setStatus('error');
          toast.error('Sessão de pagamento expirada');
        } else if (attempts < maxAttempts) {
          // Keep polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          toast.error('Não foi possível confirmar o pagamento');
        }
      } catch (error) {
        console.error('Error checking status:', error);
        if (attempts < maxAttempts) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts, navigate, updateUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-ruby mx-auto mb-6" />
            <h1 className="font-brand text-2xl font-bold text-foreground mb-4">
              Verificando pagamento...
            </h1>
            <p className="text-muted-foreground">
              Aguarda enquanto confirmamos seu pagamento.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-brand text-2xl font-bold text-foreground mb-4">
              Pagamento Confirmado!
            </h1>
            <p className="text-muted-foreground mb-2">
              Bem-vindo ao Intimate.pt Premium!
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 border border-gold/30 mb-8">
              <Crown className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold">Premium Ativo</span>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/browse')}
                data-testid="success-browse"
                className="w-full bg-ruby hover:bg-ruby-light rounded-full py-6"
              >
                Explorar Conteúdo Premium
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/home')}
                className="w-full border-border rounded-full py-6"
              >
                Ir para Início
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-brand text-2xl font-bold text-foreground mb-4">
              Erro no Pagamento
            </h1>
            <p className="text-muted-foreground mb-8">
              Não foi possível confirmar seu pagamento. 
              Por favor, tenta novamente ou entre em contato conosco.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/premium')}
                data-testid="error-retry"
                className="w-full bg-ruby hover:bg-ruby-light rounded-full py-6"
              >
                Tentar Novamente
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/home')}
                className="w-full border-border rounded-full py-6"
              >
                Voltar ao Início
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
